import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { feedApi, blogApi, syncApi, pagesApi } from '@/lib/api';

/**
 * Hook to fetch activity feed (Legacy)
 */
export function useFeed(options?: {
  source?: string;
  type?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['feed', options],
    queryFn: () => feedApi.getRecent(options),
  });
}

/**
 * Hook to fetch activity feed with infinite scroll
 */
export function useInfiniteFeed(options?: {
  source?: string;
  type?: string;
  limit?: number;
}) {
  const limit = options?.limit || 20;

  return useInfiniteQuery({
    queryKey: ['feed', 'infinite', options],
    queryFn: ({ pageParam = 0 }) => 
      feedApi.getRecent({ ...options, limit, offset: pageParam as number }),
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than limit, we've reached the end
      if (!lastPage || lastPage.length < limit) return undefined;
      
      // Calculate next offset based on total items fetched
      return allPages.flat().length;
    },
    initialPageParam: 0,
  });
}

/**
 * Hook to fetch source counts
 */
export function useSourceCounts() {
  return useQuery({
    queryKey: ['feed', 'sources'],
    queryFn: () => feedApi.getSourceCounts(),
  });
}

/**
 * Hook to fetch blog posts
 */
export function useBlogPosts(options?: {
  tag?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['blog', 'list', options],
    queryFn: () => blogApi.list(options),
  });
}

/**
 * Hook to fetch single blog post
 */
export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: ['blog', 'post', slug],
    queryFn: () => blogApi.getBySlug(slug),
    enabled: !!slug,
  });
}

/**
 * Hook to fetch blog tags
 */
export function useBlogTags() {
  return useQuery({
    queryKey: ['blog', 'tags'],
    queryFn: () => blogApi.getTags(),
  });
}

/** * Hook to fetch sync status
 */
export function useSyncStatus() {
  return useQuery({
    queryKey: ['sync', 'status'],
    queryFn: () => syncApi.getStatus(),
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

/**
 * Hook to fetch all pages
 */
export function usePages() {
  return useQuery({
    queryKey: ['pages', 'list'],
    queryFn: () => pagesApi.getAll(),
  });
}

/**
 * Hook to fetch single page content
 */
export function usePage(slug: string) {
  return useQuery({
    queryKey: ['pages', 'detail', slug],
    queryFn: () => pagesApi.getBySlug(slug),
    enabled: !!slug,
  });
}
